import { getAccessToken } from '../authentication/supabaseService';
import { PerUnitNeedsIndex } from '../../utils/reports/utilizationReport';

/**
 * Per-restaurant "needed ingredients" index for the all-restaurants Dashboard waste
 * card (New UI). The server returns the PERIOD-INDEPENDENT per-unit recipe expansion
 * (`buildPerUnitNeeds`); the frontend weights it by each period's sales (from revenue
 * it already holds) and reconciles against already-loaded orders
 * (`computeWasteTotalsFromNeeds`). So waste for every period is computed client-side —
 * the endpoint is hit once and the result cached (recipes change rarely).
 */
export interface RestaurantNeeds {
  restaurantId: string;
  restaurantName: string;
  perUnit: PerUnitNeedsIndex;
}

export interface FetchNeedsIndexParams {
  /** Comma-separated restaurant ids, or 'all' (default) for every accessible restaurant. */
  restaurantIds?: string;
  /** Bypass process-local server caches after editing a restaurant's recipes. */
  refreshRestaurantIds?: string[];
}

/**
 * Fetch the per-restaurant per-unit needs index. Throws on a non-OK response;
 * callers (the store) log the error.
 */
export async function fetchNeedsIndex(params: FetchNeedsIndexParams = {}): Promise<RestaurantNeeds[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/waste/needs-index', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      restaurantIds: params.restaurantIds ?? 'all',
      ...(params.refreshRestaurantIds?.length ? { refreshRestaurantIds: params.refreshRestaurantIds } : {}),
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch waste needs index: ${res.status} ${res.statusText}`);
  }
  const data = await res.json();
  return (data.restaurants ?? []) as RestaurantNeeds[];
}
